import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Le passage commenté, sur TheoFons.
 *
 * On mène au passage biblique et non au lectionnaire : l'app est œcuménique,
 * le lectionnaire est catholique. La page chapitre de TheoFons surligne la
 * plage donnée par l'ancre `#v{a}-{b}`, ou `#v{a}-` (jusqu'à la fin de la
 * péricope) quand le passage déborde le chapitre.
 *
 * Les références s'écrivent à la française dans les deux langues (`Lc 10, 1-12`)
 * et TheoFons nomme ses livres avec les mêmes sigles (`lc`, `1co`) — en
 * anglais aussi. TheoFons ne publie qu'en français et en anglais.
 *
 * Même logique côté serveur : functions/src/og/evangile-og.ts (theofonsPassageUrl).
 * Renvoie `null` si la référence ne se lit pas : pas de bouton plutôt qu'un
 * lien faux.
 */
export const theofonsPassageUrl = (locale: string, reference: string): string | null => {
  const m = (reference || '')
    .replace(/^\(+|\)+$/g, '')
    .trim()
    .match(/^(\d?\s?[A-Za-zÀ-ÿ]+)\s+(\d+)\s*,\s*(\d+)[a-z]*(?:\s*[-–—]\s*(\d+)[a-z]*(\s*[,-]\s*\d+)?)?/);
  if (!m) return null;
  const [, book, chapter, from, to, crossChapter] = m;
  const lang = locale === 'fr' ? 'fr' : 'en';
  const slug = book.toLowerCase().replace(/\s+/g, '');
  const anchor = !to ? `#v${from}` : crossChapter ? `#v${from}-` : `#v${from}-${to}`;
  return `https://theofons.net/${lang}/bible/${slug}/${chapter}${anchor}`;
};

/**
 * Le lectionnaire TheoFons d'un jour. Pas de slash avant `?date=` : c'est la
 * forme canonique, l'autre coûte un 301.
 */
export const theofonsLectionnaireUrl = (locale: string, date: string): string =>
  `https://theofons.net/${locale === 'fr' ? 'fr' : 'en'}/lectionnaire?date=${date}`;

interface ApprofondirButtonProps {
  locale: string;
  /** La référence commentée, telle qu'en base : `Lc 10, 1-12`. */
  reference: string;
  className?: string;
}

/**
 * Bouton « Approfondir », rangé parmi ceux de la barre du bas ; la mise en
 * page vient de `className`.
 *
 * Un vrai `<a href>`, sans `nofollow` : les robots doivent voir et suivre le
 * lien. Pour ceux qui n'exécutent pas le JavaScript, `ogEvangile` pose le même
 * lien dans le HTML servi. `noopener` seul, sans `noreferrer` : TheoFons doit
 * voir d'où vient la visite.
 */
/** Le lien aux couleurs de TheoFons, commun aux deux boutons. */
const TheofonsLink: React.FC<{ href: string; title: string; label: string; className?: string }> = ({
  href,
  title,
  label,
  className,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener"
    title={title}
    className={`bg-[#6b1f2e] text-white hover:bg-[#561824] transition-colors ${className ?? ''}`}
  >
    {/* Le logo est bordeaux sur crème : une pastille claire le garde lisible. */}
    <img src="https://theofons.net/icon-192.png" alt="" width={20} height={20} className="w-5 h-5 rounded bg-[#f7f3ee]" />
    {label}
  </a>
);

const ApprofondirButton: React.FC<ApprofondirButtonProps> = ({ locale, reference, className }) => {
  const { t } = useTranslation();
  const href = theofonsPassageUrl(locale, reference);
  if (!href) return null;
  return (
    <TheofonsLink
      href={href}
      title={t('evangile.approfondirSur', 'le passage sur TheoFons')}
      label={t('evangile.approfondir', 'Approfondir')}
      className={className}
    />
  );
};

/**
 * Le lectionnaire TheoFons du jour, pour la page liste. Les pages datées, elles,
 * mènent au passage commenté (ApprofondirButton).
 */
export const LectionnaireButton: React.FC<{ locale: string; date: string; className?: string }> = ({
  locale,
  date,
  className,
}) => {
  const { t } = useTranslation();
  const label = t('evangile.lectionnaireTheofons', 'Lectionnaire TheoFons');
  return <TheofonsLink href={theofonsLectionnaireUrl(locale, date)} title={label} label={label} className={className} />;
};

export default ApprofondirButton;
